import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Button,
  Tooltip,
  Modal,
  Input,
  Typography,
  Form,
  Upload,
  Row,
  Col,
  Select,
  Flex,
  message,
  Divider,
  Space,
  Spin,
  Progress,
} from "antd";
import {
  ArrowLeftOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  TeamOutlined,
  UserAddOutlined,
  DollarOutlined,
  CalendarOutlined,
  CommentOutlined,
  SearchOutlined,
  UploadOutlined,
  PlusOutlined,
  MinusCircleOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  LaptopOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { SubmitProfilesWrapper } from "./SubmitProfiles.style";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Title, Text } = Typography;
const { Option } = Select;

const parseExperienceRange = (range) => {
  if (!range) return null;
  if (typeof range === 'object' && range !== null) {
    const mn = parseFloat(range.min);
    const mx = parseFloat(range.max);
    if (!isNaN(mn) && !isNaN(mx)) return { min: mn, max: mx };
    if (!isNaN(mn)) return { min: mn, max: mn + 2 };
    return null;
  }
  const str = String(range).toLowerCase().replace(/years?/g, '').trim();
  const rm = str.match(/(\d+(?:\.\d+)?)\s*[-–]+\s*(\d+(?:\.\d+)?)/);
  if (rm) return { min: parseFloat(rm[1]), max: parseFloat(rm[2]) };
  const pm = str.match(/(\d+(?:\.\d+)?)\s*\+/);
  if (pm) { const v = parseFloat(pm[1]); return { min: v, max: v + 5 }; }
  const sm = str.match(/(\d+(?:\.\d+)?)/);
  if (sm) { const v = parseFloat(sm[1]); return { min: Math.max(0, v - 1), max: v + 1 }; }
  return null;
};

const computeMatchScore = (talent, job) => {
  if (!job) return 0;
  const normalize = (s) => {
    if (typeof s === "object" && s?.skill) return s.skill.toLowerCase().trim();
    return String(s || "").toLowerCase().trim();
  };

  const jobPrimary = (job.primarySkills || []).map(normalize).filter(Boolean);
  const jobSecondary = (job.secondarySkills || []).map(normalize).filter(Boolean);
  const hasPrimary = jobPrimary.length > 0;
  const hasSecondary = jobSecondary.length > 0;
  if (!hasPrimary && !hasSecondary) return 0;

  const allTalentSkills = [
    ...(talent.primarySkills || talent.topSkills || []),
    ...(talent.secondarySkills || []),
  ].map(normalize).filter(Boolean);

  const skillMatch = (ts, js) => ts.includes(js) || js.includes(ts);
  const primaryHits = jobPrimary.filter((js) => allTalentSkills.some((ts) => skillMatch(ts, js))).length;
  const secondaryHits = jobSecondary.filter((js) => allTalentSkills.some((ts) => skillMatch(ts, js))).length;

  const expRange = parseExperienceRange(job.experienceRange);
  let expScore = 0.5;
  if (expRange !== null) {
    const yrs = !isNaN(parseFloat(talent.experienceYears))
      ? parseFloat(talent.experienceYears) + (parseFloat(talent.experienceMonths) || 0) / 12
      : (() => { const m = String(talent.experience || '').match(/(\d+(?:\.\d+)?)/); return m ? parseFloat(m[1]) : null; })();
    if (yrs === null) {
      expScore = 0.5;
    } else if (yrs >= expRange.min && yrs <= expRange.max) {
      expScore = 1.0;
    } else if (yrs > expRange.max) {
      expScore = 0.8;
    } else {
      const gap = expRange.min - yrs;
      expScore = gap <= 1 ? 0.5 : gap <= 2 ? 0.25 : 0;
    }
  }

  let pct;
  if (hasPrimary && hasSecondary) {
    pct = Math.round((primaryHits / jobPrimary.length) * 60 + (secondaryHits / jobSecondary.length) * 20 + expScore * 20);
  } else if (hasPrimary) {
    pct = Math.round((primaryHits / jobPrimary.length) * 80 + expScore * 20);
  } else {
    pct = Math.round((secondaryHits / jobSecondary.length) * 80 + expScore * 20);
  }
  return Math.min(100, Math.max(0, pct));
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === '') return "₹0";
  const numValue = Number(amount);
  if (isNaN(numValue)) return "₹0";
  return `₹${numValue.toLocaleString('en-IN')}`;
};

const SubmitProfiles = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.state?.backPath || '/partner/ongoing-jobs';
  const backLabel = location.state?.backLabel || 'Back to Ongoing Jobs';
  const [jobData, setJobData] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [benchResources, setBenchResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [benchModalVisible, setBenchModalVisible] = useState(false);
  const [addResourceModalVisible, setAddResourceModalVisible] = useState(false);
  const [selectedResources, setSelectedResources] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [resume, setResume] = useState(null);
  const [aadhaar, setAadhaar] = useState(null);
  const [pan, setPan] = useState(null);
  const [degree, setDegree] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const fetchJobDetails = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_CONST.GET_JOB_REQUIREMENT}/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data.job) {
        const job = response.data.job;
        setJobData({
          id: job.id,
          role: job.role || job.title || job.designation,
          status: job.status,
          engagementType: job.engagementType,
          reportingLocation: job.reportingLocation || job.location,
          numberOfRequirements: job.numberOfRequirements || job.openPositions || 1,
          experienceRange: job.experienceRange,
          tentativeStartDate: job.tentativeStartDate,
          budget: job.budget || job.monthlyRate,
          budgetPeriod: job.budgetPeriod || 'Per Month',
          monthsOfEngagement: job.monthsOfEngagement || job.projectDuration,
          communication: job.communication,
          primarySkills: job.primarySkills || job.skills || [],
          secondarySkills: job.secondarySkills || [],
          createdAt: job.createdAt,
        });
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      message.error("Failed to load job details");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'green';
      case 'closed': return 'red';
      case 'filled': return 'blue';
      case 'draft': return 'orange';
      default: return 'default';
    }
  };

  const fetchSubmittedProfiles = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const userOrg = localStorage.getItem("userOrganization");
      
      const response = await axios.post(
        API_CONST.GET_JOB_APPLICATIONS,
        { jobId, partnerOrganization: userOrg },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.applications) {
        const mappedProfiles = response.data.applications.map((app) => ({
          id: app.applicationId,
          talentId: app.talentId,
          name: app.talent?.name || 'Unknown',
          role: app.talent?.role || 'N/A',
          skills: app.talent?.primarySkills || [],
          monthlyRate: formatCurrency(app.talent?.monthlyRate),
          experience: app.talent?.experience || 'N/A',
          noticePeriod: app.talent?.noticePeriod || 'N/A',
          resumeUrl: app.talent?.resumeUrl,
          status: app.status,
          submittedAt: app.submittedAt,
        }));
        setProfiles(mappedProfiles);
      }
    } catch (error) {
      console.error("Error fetching submitted profiles:", error);
    }
  };

  const fetchBenchPool = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const userOrg = localStorage.getItem("userOrganization");
      
      const response = await axios.post(
        API_CONST.GET_TALENT_PROFILES,
        { organization: userOrg, status: 'Active' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.Response) {
        const mappedResources = response.data.Response.map((talent) => ({
          id: talent.id,
          name: talent.name,
          role: talent.role,
          topSkills: talent.primarySkills || [],
          primarySkills: talent.primarySkills || [],
          secondarySkills: talent.secondarySkills || [],
          monthlyRate: formatCurrency(talent.partnerRate || talent.rate),
          experience: talent.experience || `${talent.experienceYears || 0}.${talent.experienceMonths || 0} Year`,
          status: talent.status || 'Active',
        }));
        setBenchResources(mappedResources);
      }
    } catch (error) {
      console.error("Error fetching bench pool:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchJobDetails(), fetchSubmittedProfiles(), fetchBenchPool()]);
      setLoading(false);
    };
    
    if (jobId) {
      loadData();
    }
  }, [jobId]);

  if (loading) {
    return (
      <SubmitProfilesWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div>Loading...</div>
        </div>
      </SubmitProfilesWrapper>
    );
  }

  if (!jobData) {
    return (
      <SubmitProfilesWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h2>Job not found</h2>
          <Button
            type="primary"
            onClick={() => navigate(backPath)}
          >
            {backLabel}
          </Button>
        </div>
      </SubmitProfilesWrapper>
    );
  }

  const handleBenchPoolClick = async () => {
    setSelectedResources([]);
    await fetchBenchPool();
    setBenchModalVisible(true);
  };

  const handleAddResourceClick = () => {
    navigate(`/partner/add-talent?submitToJob=${jobId}`);
  };

  const handleDownloadResume = async (resumeUrl) => {
    if (!resumeUrl) {
      message.warning("No resume available for this profile");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_DOCUMENT_URL,
        { objectPath: resumeUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, "_blank");
      } else {
        message.error("Could not get download URL");
      }
    } catch (error) {
      console.error("Error getting resume URL:", error);
      message.error("Failed to get resume URL");
    }
  };

  const handleBenchModalClose = () => {
    setBenchModalVisible(false);
    setSearchText("");
    setSelectedResources([]);
  };

  const handleAddResourceModalClose = () => {
    setAddResourceModalVisible(false);
    form.resetFields();
    setResume(null);
    setAadhaar(null);
    setPan(null);
    setDegree(null);
    setCurrentStep(0);
  };

  const handleNext = () => {
    form.validateFields().then(() => {
      setCurrentStep(1);
    }).catch((error) => {
      console.log("Validation failed:", error);
    });
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  const handleResourceSelection = (record, selected) => {
    if (selected) {
      setSelectedResources([...selectedResources, record]);
    } else {
      setSelectedResources(selectedResources.filter((r) => r.id !== record.id));
    }
  };

  const handleSelectAll = (selected, selectedRows) => {
    if (selected) {
      setSelectedResources(selectedRows);
    } else {
      setSelectedResources([]);
    }
  };

  const handleRemoveSelected = (resourceId) => {
    setSelectedResources(selectedResources.filter((r) => r.id !== resourceId));
  };

  const handleAddResources = async () => {
    if (selectedResources.length === 0) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("authToken");
      const userOrg = localStorage.getItem("userOrganization");
      const talentIds = selectedResources.map((r) => r.id);
      
      const response = await axios.post(
        API_CONST.SUBMIT_PROFILES_TO_JOB,
        { jobId, talentIds, partnerOrganization: userOrg },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.submitted && response.data.submitted.length > 0) {
        message.success(`Successfully submitted ${response.data.submitted.length} profile(s)`);
        await fetchSubmittedProfiles();
        await fetchBenchPool();
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach((err) => {
          message.warning(`${err.talentId}: ${err.error}`);
        });
      }
      
      setBenchModalVisible(false);
      setSearchText("");
      setSelectedResources([]);
    } catch (error) {
      console.error("Error submitting profiles:", error);
      message.error("Failed to submit profiles");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitNewResource = async (values) => {
    const data = new FormData();
    if (resume) data.append("resume", resume);
    if (aadhaar) data.append("Aadhaar", aadhaar);
    if (pan) data.append("pan", pan);
    if (degree) data.append("degree", degree);
    if (values) data.append("data", JSON.stringify(values));

    try {
      const response = await axios.post(API_CONST.ADD_TALENT_PROFILE, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("Resource added successfully!");
      console.log(response.data);
    } catch (error) {
      console.error(error);
    }

    handleAddResourceModalClose();
  };

  const handleWithdrawProfile = (record) => {
    Modal.confirm({
      title: "Withdraw Profile",
      content: `Are you sure you want to withdraw ${record.name}'s profile from this job?`,
      okText: "Yes, Withdraw",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.delete(`${API_CONST.WITHDRAW_JOB_APPLICATION}/${record.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success(`${record.name}'s profile has been withdrawn successfully`);
          await fetchSubmittedProfiles();
        } catch (error) {
          console.error("Error withdrawing profile:", error);
          message.error("Failed to withdraw profile");
        }
      },
    });
  };

  const submittedTalentIds = profiles.map((p) => p.talentId);
  const availableBenchResources = benchResources.filter(
    (resource) => !submittedTalentIds.includes(resource.id) && resource.status !== 'Hired'
  );

  const filteredBenchResources = availableBenchResources
    .filter(
      (resource) =>
        resource.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        resource.role?.toLowerCase().includes(searchText.toLowerCase()) ||
        (resource.topSkills || []).some((s) => {
          const skillName = typeof s === "object" ? s.skill : s;
          return skillName?.toLowerCase().includes(searchText.toLowerCase());
        })
    )
    .map((resource) => ({
      ...resource,
      matchScore: computeMatchScore(resource, jobData),
    }))
    .sort((a, b) => b.matchScore - a.matchScore);

  const rowSelection = {
    selectedRowKeys: selectedResources.map((r) => r.id),
    onSelect: handleResourceSelection,
    onSelectAll: handleSelectAll,
  };

  const benchColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 160,
      render: (text, record) => (
        <a
          href={`/partner/talent-details/${record.id}`}
          style={{ color: "#1890ff", fontWeight: 600, fontSize: "13px" }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/partner/talent-details/${record.id}`);
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 180,
      render: (text) => <Text style={{ color: "#595959", fontSize: "13px" }}>{text}</Text>,
    },
    {
      title: "Top Skills",
      dataIndex: "topSkills",
      key: "topSkills",
      width: 260,
      render: (skills, record) => {
        const skillsArray = skills || [];
        if (skillsArray.length === 0) {
          return <Text style={{ color: "#999", fontSize: "12px" }}>No skills</Text>;
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {skillsArray.map((skill, index) => (
              <Tag key={index} color="cyan" style={{ marginBottom: 4, fontSize: "12px" }}>
                {typeof skill === 'object' ? `${skill.skill} - ${skill.level}` : String(skill)}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Monthly Rate",
      dataIndex: "monthlyRate",
      key: "monthlyRate",
      width: 130,
      render: (text) => (
        <Text strong style={{ color: "#00d9a9", fontSize: "13px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: 110,
      render: (text) => <Text style={{ fontSize: "13px" }}>{text}</Text>,
    },
    {
      title: "Match Score",
      dataIndex: "matchScore",
      key: "matchScore",
      width: 130,
      sorter: (a, b) => a.matchScore - b.matchScore,
      defaultSortOrder: "descend",
      render: (score) => {
        const color = score >= 70 ? "#52c41a" : score >= 40 ? "#faad14" : "#bfbfbf";
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Progress
              percent={score}
              size="small"
              strokeColor={color}
              showInfo={false}
              style={{ margin: 0 }}
            />
            <Text style={{ fontSize: "11px", color, fontWeight: 600, textAlign: "center" }}>
              {score}% Match
            </Text>
          </div>
        );
      },
    },
  ];

  const columns = [
    {
      title: "Resume",
      dataIndex: "resumeUrl",
      key: "resume",
      width: 80,
      render: (url) => (
        <Tooltip title="View Resume">
          <FilePdfOutlined
            style={{ fontSize: 24, color: "#ff4d4f", cursor: "pointer" }}
            onClick={() => window.open(url, "_blank")}
          />
        </Tooltip>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      render: (name) => (
        <span style={{ color: "#014c75", fontWeight: 500 }}>{name}</span>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 180,
    },
    {
      title: "Top Skills",
      dataIndex: "skills",
      key: "skills",
      width: 300,
      render: (skills) => {
        const skillsArray = skills || [];
        if (skillsArray.length === 0) {
          return <Text style={{ color: "#999", fontSize: "12px" }}>No skills</Text>;
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {skillsArray.map((skill, index) => (
              <Tag key={index} color="cyan" style={{ margin: "2px 0" }}>
                {typeof skill === 'object' ? `${skill.skill} - ${skill.level}` : String(skill)}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Monthly Rate",
      dataIndex: "monthlyRate",
      key: "monthlyRate",
      width: 120,
      render: (rate) => (
        <span style={{ fontWeight: 600, color: "#00d9a9" }}>{rate}</span>
      ),
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: 100,
    },
    {
      title: "Notice Period",
      dataIndex: "noticePeriod",
      key: "noticePeriod",
      width: 120,
    },
    {
      title: "Action",
      key: "action",
      width: 180,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Tooltip title="Download Resume">
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownloadResume(record.resumeUrl)}
            />
          </Tooltip>
          <Tooltip title="Withdraw Profile">
            <Button
              danger
              size="small"
              onClick={() => handleWithdrawProfile(record)}
            >
              Withdraw
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <SubmitProfilesWrapper>
      <div className="header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backPath)}
          className="back-button"
        >
          {backLabel}
        </Button>
        <h2>Submit Profiles</h2>
      </div>

      <Card className="job-summary-card">
        <div className="job-header">
          <div>
            <div className="job-id">Job ID: {jobData.id}</div>
            <h2 className="job-title">{jobData.role}</h2>
            <div className="job-meta">
              <Tag color={getStatusColor(jobData.status)}>{jobData.status?.toUpperCase()}</Tag>
              <Tag color="blue">{jobData.engagementType}</Tag>
              <Tag color="cyan" icon={<EnvironmentOutlined />}>{jobData.reportingLocation}</Tag>
              <Tag color="green" icon={<TeamOutlined />}>Requirements: {jobData.numberOfRequirements}</Tag>
            </div>
          </div>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/partner/job-full-details/${jobData.id}`, {
              state: { backPath: `/partner/submit-profiles/${jobData.id}`, readOnly: true, partnerView: true }
            })}
            style={{ padding: 0, height: "auto", fontSize: 14 }}
          >
            Detailed View
          </Button>
        </div>

        <div className="metrics-row">
          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#e6f7ff" }}>
              <DollarOutlined style={{ fontSize: 24, color: "#1890ff" }} />
            </div>
            <div className="metric-info">
              <h3>{formatCurrency(jobData.budget)}</h3>
              <p>{jobData.budgetPeriod || 'Per Month'}</p>
            </div>
          </div>

          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#fff7e6" }}>
              <CalendarOutlined style={{ fontSize: 24, color: "#fa8c16" }} />
            </div>
            <div className="metric-info">
              <h3>{jobData.monthsOfEngagement || '-'}</h3>
              <p>Engagement Duration</p>
            </div>
          </div>

          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#f6ffed" }}>
              <ClockCircleOutlined style={{ fontSize: 24, color: "#52c41a" }} />
            </div>
            <div className="metric-info">
              <h3>{jobData.experienceRange || '-'}</h3>
              <p>Experience Required</p>
            </div>
          </div>

          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#fff1f0" }}>
              <CommentOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
            </div>
            <div className="metric-info">
              <h3>{jobData.communication || '-'}</h3>
              <p>Communication Level</p>
            </div>
          </div>
        </div>

        <Row gutter={[24, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Primary Skills:</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.primarySkills || []).map((skill, index) => (
                  <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                    {typeof skill === 'object' ? `${skill.skill} - ${skill.level}` : skill}
                  </Tag>
                ))}
                {(!jobData.primarySkills || jobData.primarySkills.length === 0) && (
                  <span style={{ color: "#8c8c8c" }}>None specified</span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Secondary Skills:</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.secondarySkills || []).map((skill, index) => (
                  <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                    {skill}
                  </Tag>
                ))}
                {(!jobData.secondarySkills || jobData.secondarySkills.length === 0) && (
                  <span style={{ color: "#8c8c8c" }}>None specified</span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Start Date:</span>
              <div style={{ marginTop: 4, fontWeight: 500 }}>{formatDate(jobData.tentativeStartDate)}</div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Created On:</span>
              <div style={{ marginTop: 4, fontWeight: 500 }}>{formatDate(jobData.createdAt)}</div>
            </div>
          </Col>
        </Row>
      </Card>

      <div className="profiles-section">
        <div className="profiles-header">
          <h3 className="section-title">
            Profiles Submitted ({profiles.length})
          </h3>
          <div className="action-buttons">
            <Button
              type="default"
              icon={<TeamOutlined />}
              size="large"
              className="bench-pool-btn"
              onClick={handleBenchPoolClick}
            >
              Add from Bench Pool
            </Button>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              size="large"
              className="add-resource-btn"
              onClick={handleAddResourceClick}
            >
              Add New Resource
            </Button>
          </div>
        </div>

        <Card className="profiles-table-card">
          <Table
            columns={columns}
            dataSource={profiles}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            scroll={{ x: 1200 }}
            locale={{
              emptyText:
                "No profiles submitted yet. Click 'Add from Bench Pool' or 'Add New Resource' to submit profiles.",
            }}
          />
        </Card>
      </div>

      <Modal
        open={benchModalVisible}
        onCancel={handleBenchModalClose}
        footer={null}
        width={920}
        className="bench-pool-modal"
      >
        <Flex
          justify="center"
          vertical
          align="center"
          style={{ borderBottom: "1px solid #e8e8e8", marginBottom: "24px", paddingBottom: "16px" }}
        >
          <Text style={{ fontSize: "28px", fontWeight: 600, color: "#014c75" }}>
            Add Resource from my resources
          </Text>
        </Flex>
        <div className="modal-content">
          <div className="search-wrapper">
            <Input
              placeholder="Search by name, role, or skill"
              prefix={<SearchOutlined style={{ color: "#bfbfbf", fontSize: "16px" }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
              className="modal-search"
              allowClear
              style={{
                borderRadius: "8px",
                border: "1px solid #d9d9d9",
                fontSize: "14px",
              }}
            />
          </div>

          <Table
            columns={benchColumns}
            dataSource={filteredBenchResources}
            rowKey="id"
            rowSelection={rowSelection}
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            scroll={{ y: 320 }}
            className="bench-table"
            size="middle"
          />

          {selectedResources.length > 0 && (
            <div className="selected-section">
              <Text strong style={{ color: "#014c75", fontSize: "15px", marginBottom: "12px", display: "block" }}>
                Resources Selected: {selectedResources.length}
              </Text>
              <div className="selected-tags">
                {selectedResources.map((resource) => (
                  <Tag
                    key={resource.id}
                    closable
                    onClose={() => handleRemoveSelected(resource.id)}
                    className="selected-tag"
                    style={{
                      padding: "6px 12px",
                      fontSize: "13px",
                      borderRadius: "6px",
                      background: "#e6fff9",
                      border: "1px solid #00d9a9",
                      color: "#014c75",
                      marginBottom: "8px",
                    }}
                  >
                    {resource.name}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          <div className="modal-footer">
            <Button
              type="primary"
              size="large"
              onClick={handleAddResources}
              disabled={selectedResources.length === 0}
              className="add-resource-btn-modal"
            >
              Add Resource
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={addResourceModalVisible}
        onCancel={handleAddResourceModalClose}
        footer={null}
        width={900}
        className="add-resource-modal"
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto", padding: "24px" }}
      >
        <Flex
          justify="center"
          vertical
          align="center"
          style={{
            borderBottom: "1px solid #e8e8e8",
            marginBottom: "24px",
            paddingBottom: "16px",
          }}
        >
          <Text style={{ fontSize: "28px", fontWeight: 600, color: "#014c75" }}>
            Add Bench Resource {currentStep === 0 ? "(Step 1/2)" : "(Step 2/2)"}
          </Text>
        </Flex>
        <Form form={form} layout="vertical" onFinish={handleSubmitNewResource}>
          {/* Step 1: Basic Information */}
          {currentStep === 0 && (
            <>
          <Form.Item label="Upload Resume">
            <Upload
              beforeUpload={(file) => {
                setResume(file);
                return false;
              }}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
            {resume && (
              <div style={{ marginTop: "10px" }}>
                {resume.name}{" "}
                <Button onClick={() => setResume(null)} size="small">
                  ✖
                </Button>
              </div>
            )}
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Please enter the name!" }]}
              >
                <Input placeholder="Enter Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Role"
                name="role"
                rules={[{ required: true, message: "Please select a role!" }]}
              >
                <Select placeholder="Select Role">
                  <Option value="Software Engineer">Software Engineer</Option>
                  <Option value="Project Manager">Project Manager</Option>
                  <Option value="Full Stack Developer">
                    Full Stack Developer
                  </Option>
                  <Option value="DevOps Engineer">DevOps Engineer</Option>
                  <Option value="Python Developer">Python Developer</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Experience">
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item name="experienceYears" noStyle>
                      <Input placeholder="Years" type="number" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="experienceMonths" noStyle>
                      <Input placeholder="Months" type="number" />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item label="Technical Skills">
            <Form.List name="skills">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row key={key} gutter={16} style={{ marginBottom: 8 }}>
                      <Col span={11}>
                        <Form.Item
                          {...restField}
                          name={[name, 'skill']}
                          rules={[{ required: true, message: 'Please enter skill name' }]}
                        >
                          <Input placeholder="Skill Name (e.g., React, Python)" />
                        </Form.Item>
                      </Col>
                      <Col span={11}>
                        <Form.Item
                          {...restField}
                          name={[name, 'level']}
                          rules={[{ required: true, message: 'Please select proficiency' }]}
                        >
                          <Select placeholder="Proficiency Level">
                            <Option value="Expert">Expert</Option>
                            <Option value="Advanced">Advanced</Option>
                            <Option value="Intermediate">Intermediate</Option>
                            <Option value="Beginner">Beginner</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ color: '#ff4d4f', fontSize: '18px', marginTop: '8px' }}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Skill
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Monthly Rate" name="rate">
                <Input placeholder="Enter Monthly Rate" prefix="₹" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Notice Period" name="notice">
                <Input placeholder="Enter Notice Period (e.g., 30 days)" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Email" name="email">
                <Input placeholder="Enter email address" type="email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Phone" name="phone">
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Hourly Rate" name="hourlyRate">
                <Input placeholder="Enter Hourly Rate" prefix="₹" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Location" name="location">
                <Select placeholder="Select Location">
                  <Option value="Bangalore">Bangalore</Option>
                  <Option value="Mumbai">Mumbai</Option>
                  <Option value="Pune">Pune</Option>
                  <Option value="Hyderabad">Hyderabad</Option>
                  <Option value="Delhi">Delhi</Option>
                  <Option value="Chennai">Chennai</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Availability" name="availability">
                <Select placeholder="Select Availability">
                  <Option value="Immediately Available">Immediately Available</Option>
                  <Option value="Available in 2 weeks">Available in 2 weeks</Option>
                  <Option value="Available in 1 month">Available in 1 month</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          </>
          )}

          {/* Step 2: Optional Information */}
          {currentStep === 1 && (
            <>
          <Form.Item label="Professional Summary (Optional)" name="summary">
            <Input.TextArea 
              rows={4} 
              placeholder="Enter professional summary highlighting key skills, experience, and expertise..." 
            />
          </Form.Item>
          
          <Form.Item label="Project Experience (Optional)">
            <Form.List name="projects">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginBottom: 16, background: '#fafafa' }}
                      extra={
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ color: '#ff4d4f', fontSize: '16px' }}
                        />
                      }
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'title']}
                            label="Project Title"
                            rules={[{ required: true, message: 'Please enter project title' }]}
                          >
                            <Input placeholder="e.g., E-commerce Platform Development" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'client']}
                            label="Client"
                          >
                            <Input placeholder="e.g., Fortune 500 Company" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'duration']}
                            label="Duration"
                          >
                            <Input placeholder="e.g., 6 months" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'role']}
                            label="Your Role"
                          >
                            <Input placeholder="e.g., Lead Developer" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'technologies']}
                            label="Technologies"
                          >
                            <Input placeholder="e.g., React, Node.js, MongoDB" />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        {...restField}
                        name={[name, 'description']}
                        label="Description"
                      >
                        <Input.TextArea
                          rows={3}
                          placeholder="Describe the project, your contributions, and achievements..."
                        />
                      </Form.Item>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Project
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Upload Aadhaar Card">
                <Upload
                  beforeUpload={(file) => {
                    setAadhaar(file);
                    return false;
                  }}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>Upload Aadhaar</Button>
                </Upload>
                {aadhaar && (
                  <div style={{ marginTop: "10px" }}>
                    {aadhaar.name}{" "}
                    <Button onClick={() => setAadhaar(null)} size="small">
                      ✖
                    </Button>
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Upload PAN Card">
                <Upload
                  beforeUpload={(file) => {
                    setPan(file);
                    return false;
                  }}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>Upload PAN</Button>
                </Upload>
                {pan && (
                  <div style={{ marginTop: "10px" }}>
                    {pan.name}{" "}
                    <Button onClick={() => setPan(null)} size="small">
                      ✖
                    </Button>
                  </div>
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Upload Degree Proof">
                <Upload
                  beforeUpload={(file) => {
                    setDegree(file);
                    return false;
                  }}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>Upload Degree</Button>
                </Upload>
                {degree && (
                  <div style={{ marginTop: "10px" }}>
                    {degree.name}{" "}
                    <Button onClick={() => setDegree(null)} size="small">
                      ✖
                    </Button>
                  </div>
                )}
              </Form.Item>
            </Col>
          </Row>
          </>
          )}

          {/* Action Buttons */}
          <Form.Item style={{ marginTop: "24px", marginBottom: 0 }}>
            <Flex justify="flex-end" gap={12}>
              <Button onClick={handleAddResourceModalClose}>Cancel</Button>
              {currentStep === 0 && (
                <Button type="primary" onClick={handleNext}>
                  Next
                </Button>
              )}
              {currentStep === 1 && (
                <>
                  <Button onClick={handleBack}>Back</Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
                  >
                    Submit
                  </Button>
                </>
              )}
            </Flex>
          </Form.Item>
        </Form>
      </Modal>
    </SubmitProfilesWrapper>
  );
};

export default SubmitProfiles;
